// AskIAM UI Theme - Color Palette
// Extracted from the AskIT identity & access support portal design
//
// Every value here that text is drawn in, or drawn on, is chosen to clear WCAG
// 2.2 AAA (7:1) against BOTH surfaces the app actually uses: background.paper
// (#ffffff) and background.default (#f5f5f5). #f5f5f5 is the harder of the two
// and is the one that matters, because most text sits on the page rather than
// on a card - a value tuned only against white passes in isolation and still
// fails everywhere it is really used.
//
// The target is AA. These clear AAA because the gap between them turned out to
// be one shade of the same hue, not a redesign: every value below is the
// original colour scaled toward black until it cleared 7:1, so the app still
// reads as the same palette. Headroom above the gate is what stops a later
// tweak - a slightly lighter surface, a tint, a hover state - from quietly
// dropping a value back under 4.5.
//
// Contrast is symmetric: (L1+0.05)/(L2+0.05) does not care which colour is the
// text. A value that clears 7:1 as text on white also clears it as a background
// under white text, so the contained-button and chip cases are already covered
// by the same numbers.
//
// SCOPE: the block below is light mode; `darkMode` is its own solved set, and
// theme.ts picks between them. The split is enforced rather than hoped for, so
// darkening a colour for light can no longer drag dark down with it.
//
// Dark mode is no longer unmeasured. It was, for the whole life of this file -
// the scan had no way to switch theme and reported a light/dark sweep it never
// performed. Allyance v1.11.2 plus the theme storage key made it real, and the
// first honest dark scan found 30 must-fix defects in the block below. Both
// sets are now measured; neither is assumed.
//
// One caveat survives: MUI composites an elevation overlay onto dark surfaces,
// so #121212 is the page, not the background under every element. The dark
// values are solved against the #121212 the scan measured and then floored
// against a hand-computed #383838 so an elevated dialog still clears AA. The
// hand-computed figure is a floor to check against, never the target.

export const palette = {
  primary: {
    // #0078d4 -> #0072c9 (AA) -> #005597 -> #004e8b. Every semantic colour in
    // this block is solved against all three light surfaces it can land on:
    // paper #ffffff, page #f5f5f5, and a chip's #ebebeb. Solving against white
    // alone left error, success and info at 6.44-6.48 on the page surface - the
    // same mistake text.secondary made, and the reason that comment exists.
    // Contrast is symmetric, so these numbers also cover white-on-primary
    // buttons and chips. 8.53 / 7.83 / 7.16.
    main: '#004e8b',
    // Was #005a9e, which after the step above was *lighter* than main - a
    // "dark" variant that MUI uses for hover would have made the control
    // recede on press. 10.34 on white.
    dark: '#004274',
    light: '#e3f2fd',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#1a1a2e',
    dark: '#16213e',
    light: '#2a2a4e',
    contrastText: '#ffffff',
  },
  error: {
    // 4.98 -> 7.02 on white -> 8.54 / 7.83 / 7.16 on paper/page/chip.
    main: '#922121',
    // Not just a tint: MUI derives the standard Alert's text from this token as
    // darken(light, 0.6) and its background as lighten(light, 0.9). The four
    // near-miss Alert greys (#665e5f and friends, 6.0-6.3) came from here, not
    // from `main`. They are corrected in theme.ts's MuiAlert overrides rather
    // than by darkening `light`, which would repaint every alert's tint.
    light: '#ffebee',
  },
  warning: {
    // #ed6c02 -> #b65302 (AA) -> #883e01 -> #7d3901. 8.54 / 7.83 / 7.16.
    main: '#7d3901',
    light: '#fff3e0',
  },
  success: {
    // 5.13 -> 7.07 on white -> 8.54 / 7.84 / 7.17 on paper/page/chip.
    main: '#205723',
    light: '#e8f5e9',
  },
  info: {
    // #0288d1 -> #0276b6 (AA) -> #025d90 -> #02517d. 8.54 / 7.83 / 7.16.
    main: '#02517d',
    light: '#e1f5fe',
  },
  // Dark mode, now measured rather than assumed. Until Allyance v1.11.2 the
  // harness had no way to switch theme, so a three-way "light/dark/mobile"
  // sweep was three light scans under three labels and this block was never
  // looked at. The first honest dark scan (build 51218) found 30 must-fix
  // defects here, including a Chip label at **1.09** - text all but invisible.
  //
  // The rule that governs this block is the opposite of the one above: on a
  // dark surface, contrast comes from being LIGHTER, so these are tints, not
  // shades, and `dark` (which MUI uses for hover) is lighter than `main` so a
  // control advances on press instead of receding.
  //
  // Each value is solved against the surface axe actually measured, #121212,
  // to 7:1 - and then floored so it still clears AA 4.5 on #383838, the most
  // elevated surface MUI's overlay produces (dialogs and menus). Solving to
  // exactly 7:1 on the page left menus at 4.39. The elevated figures are used
  // only as a floor to check against, never as the target, because MUI's
  // overlay is composited at render time and hand-computing it is guesswork -
  // #121212 is the one number the scan handed us.
  //
  // A single colour cannot serve as both light text on the page and a filled
  // background under white text: white-on-swatch at 7:1 needs luminance <= 0.10
  // and swatch-on-#121212 at 7:1 needs >= 0.34. So `contrastText` flips to
  // near-black here, which is also what MUI's own dark palettes do.
  darkMode: {
    // Text, links and tabs read this on #121212 - 19 button, 5 tab and 1 link
    // finding at 3.79 before this change. 7.19 on the page, 4.50 on a dialog.
    primary: { main: '#59a7e3', dark: '#77b7e8', light: '#e3f2fd', contrastText: '#121212' },
    // Added, and the reason for the worst defect in the app. theme.ts passed
    // `palette.secondary` in BOTH modes - it was the one token that never
    // switched - so dark mode painted Chip labels in #1a1a2e, a near-black
    // navy built for the sidebar, onto #121212: contrast 1.09 across 31
    // elements. Lightened from the navy so the family still reads as the same
    // colour. 7.19 on the page. `light` is a pale tint like primary's, not the
    // navy `#2a2a4e` this first carried: nothing reads it today, but at 1.37 on
    // `#121212` it was a trap waiting for the first component that did. 15.89.
    secondary: { main: '#9f9fb0', dark: '#b5b5c3', light: '#ececef', contrastText: '#121212' },
    // 3.76 -> 7.21.
    //
    // `dark` is explicit on every role here, not just primary and secondary.
    // When it is absent MUI fills it in as darken(main, 0.3), which on a dark
    // surface is backwards twice over: the control recedes on press, and
    // near-black contrastText on the hover fill drops to 3.80 - below AA.
    // Measured for all four: error 3.80, warning 3.79, success 3.80, info 3.79.
    // Each is lightened by the same 0.18 step primary uses, giving 8.58-8.78.
    // axe never hovers, so nothing would ever have reported this.
    error: { main: '#e58686', dark: '#ea9c9c', light: '#ffebee', contrastText: '#121212' },
    // 3.77 as text, and 4.96 under white on a filled Chip. Now 7.22 either way.
    warning: { main: '#f0852c', dark: '#f39b52', light: '#fff3e0', contrastText: '#121212' },
    // 3.65 -> 7.23.
    success: { main: '#7aad7d', dark: '#92bc94', light: '#e8f5e9', contrastText: '#121212' },
    // 7.22.
    info: { main: '#49aade', dark: '#6ab9e4', light: '#e1f5fe', contrastText: '#121212' },
    // `light` is deliberately left as the pale tint. In dark mode MUI derives an
    // Alert's text as lighten(light, 0.6) and its background as darken(light,
    // 0.9) - near-white on near-black - which the scan already reports clean.
    // Changing it to "fix" the tint would repaint every alert in dark mode.
  },
  background: {
    default: '#f5f5f5',
    paper: '#ffffff',
  },
  text: {
    primary: '#333333',
    // #666666 (AA) -> #535353 (AAA on the two page surfaces) -> #4d4d4d. The
    // previous step cleared white and #f5f5f5 but not the tinted surfaces this
    // text also lands on: 6.45 on a chip's #ebebeb and 6.91 inside an info
    // Alert's #ebf4fb. Solving against all four instead of the obvious two
    // gives 8.45 / 7.75 / 7.09 / 7.59. Tuning against only the surfaces that
    // happened to appear in a scan is how the first attempt came up short.
    secondary: '#4d4d4d',
    // "Disabled" describes the control's state, not permission to be
    // unreadable. #999999 (2.84) -> #707070 (AA) -> #535353: 7.69 / 7.06.
    disabled: '#535353',
  },
  focus: {
    // The 21:1 pair remains distinct on both light surfaces and the dark header/sidebar.
    border: '#000000',
    halo: '#ffffff',
  },
  sidebar: {
    // Was linear-gradient(180deg, #1a1a2e 0%, #16213e 100%). A gradient cannot
    // be contrast-checked: axe reports every element sitting on one as
    // "background could not be determined", which on this app meant 6,113
    // elements were neither passing nor failing - they were simply unknown,
    // and unknown is not clean. Both endpoints were measured before choosing:
    // the darker one is used, so contrast improves everywhere rather than
    // degrading anywhere, and the surface becomes answerable.
    background: '#16213e',
    text: 'rgba(255, 255, 255, 0.7)',
    // For the quieter labels: the section headings and the external-link
    // captions. Both were white at low alpha (0.55 and 0.5), which composites
    // to #969ba8 (5.71) and #8b909f (4.98) - between them 807 of the app's
    // 1,268 enhanced-contrast nodes, by far the largest single block. Stated as
    // a solid hex rather than another alpha because a composited colour is a
    // value nobody can read off the source: the two sites above were tuned by
    // alpha and landed on different, unintended ratios. 8.24 on the sidebar.
    //
    // Solved against the hover composite, not the base surface. The footer and
    // the nav rows paint hoverBg - white at 0.05 - which lightens #16213e to
    // #222c48, and light text on a lightened dark surface loses contrast. At
    // #a8acb7 this read 7.00 at rest and 6.08 under the cursor, so the AAA
    // claim would have been true only until someone pointed at it. axe never
    // hovers, so no scan would ever have reported it: the previous alpha was
    // 4.32 on hover, a real AA failure that sat there unmeasured. Solving
    // against the worst state makes the rest state free.
    textMuted: '#b7bbc4',
    // The sidebar is dark, so the brand blue cannot be reused here: #0078d4
    // scores 2.83 against the active row (activeBg composited over the sidebar
    // background). Fluent 2's dark-theme brand blue #479ef5 fixed that at 4.57,
    // which clears AA and not AAA; lightened one step to 7.05 there and 8.73 on
    // the plain sidebar. On a dark surface the safe direction is lighter, which
    // is the opposite of every other value in this file - worth knowing before
    // "consistently" darkening it.
    textActive: '#90c5f9',
    textHover: '#ffffff',
    // The 3px active-item indicator. It used primary.main, which is a colour
    // chosen against white and therefore the wrong tool on this surface: at
    // #0072c9 it was already only 2.59 against the active row, below the 3:1
    // that WCAG 1.4.11 asks of a non-text indicator, and taking primary.main to
    // AAA on white (#005597) dropped it to 1.67. Darkening for one surface
    // silently damages the other, which is the whole reason the sidebar carries
    // its own tokens. Matching textActive keeps the indicator and the label the
    // same colour and measures 8.73 on the sidebar, 7.03 on the active row.
    activeBorder: '#90c5f9',
    activeBg: 'rgba(0, 120, 212, 0.2)',
    hoverBg: 'rgba(255, 255, 255, 0.05)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
  },
  header: {
    // Pixel-sampled from the reference internal tool's actual header
    // (#0f6cbd), not the brighter #0078d4 Fluent "Communication Blue" this
    // file once replaced for the same reason stated below. #0f6cbd is
    // deliberately darker/more muted to match that tool's flat, lean header
    // colour, per explicit product sign-off. White on #0f6cbd measures 5.38 -
    // AA (and closer to the old #005a9e's 7.10 AAA than a brighter blue would
    // have been). Noting the trade-off here so it reads as a choice, not a
    // regression, the next time someone scans this file's contrast history.
    background: '#0f6cbd',
    // This header is one flat colour in both themes, but Header's AppBar has
    // no explicit `color` prop, so without this its text/icons (all
    // `color="inherit"`) fell through to MUI's colorPrimary class, which
    // reads mode-aware palette.primary.contrastText - #121212 in dark mode.
    // #121212 on this background measures 2.64 (old #005a9e) / 3.48 (this
    // #0f6cbd): already failing AA before this change, still failing after.
    // Forcing white keeps it flat at 5.38 - matching the background comment
    // above - in both modes.
    text: '#ffffff',
  },
  divider: '#eeeeee',
} as const;
